/**
 * This component is an input for first and last name, along with a dropdown of
 * countries to choose from. When a user sends more than $15 to a non-PayPal email
 * or phone, we render this component and make them fill out the name and country
 * of the recipient to whom they are sending
 */
import PropTypes from 'prop-types'

import React from 'react'
import { connect } from 'transfer/utils/p2p-connect'
import {
  Row,
  Col,
  TextInput as PPTextInput,
  DropdownMenu,
} from '@paypalcorp/pp-react'
import TextInput from 'react-text-input'
import * as UnilateralActions from '../actions/unilateral'
import glamorous from 'glamorous'
import { css } from 'glamor'
import content from 'pp-react-l10n'
import serverProps from '../utils/serverProps'
import enRTRStrings from '../../../../config/enRTRStrings'
import { isDw2Repaint } from '../../experiments/dw2Repaint'
import { BOX_BORDER_STYLES } from 'lib/styles/styles'

let i18n = content('transfer/send/recipientInfo')

const Container = glamorous.div({
  background: 'white',
  border: '1px solid #f2f2f2',
  padding: '1em',
  ...(isDw2Repaint() && { ...BOX_BORDER_STYLES }),
})

const rowMarginBottom = css({ marginBottom: '1em' }).toString()

const STYLES = {
  rtrSubText: {
    fontSize: '14px',
    color: '#687173',
    lineHeight: '20px',
    marginBottom: '8px',
  },
}

class UnilateralFields extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    countryCode: PropTypes.string.isRequired,
    griffinSupportedCountries: PropTypes.array.isRequired,
    styleOverrides: PropTypes.object,
    showENSubTextRTR: PropTypes.bool,
    isMgm: PropTypes.bool,
  }

  handleChangeName = event => {
    this.props.dispatch(
      UnilateralActions.changeUnilateralName(event.target.value)
    )
  }

  handleChangeCountryCode = event => {
    this.props.dispatch(
      UnilateralActions.changeUnilateralCountryCode(event.target.value)
    )
  }

  render() {
    const { cannotReceiveCountries = [] } = serverProps
    const { styleOverrides = {}, showENSubTextRTR, isMgm } = this.props
    let options
    if (isMgm) {
      options = this.props.griffinSupportedCountries
        .filter(
          /* do not show in the dropdown countries we shouldn't send */
          country => country.countryCode === this.props.countryCode
        )
        .map(country => {
          const { countryCode, countryName } = country
          return (
            <option key={countryCode} value={countryCode}>
              {countryName}
            </option>
          )
        })
    } else {
      options = this.props.griffinSupportedCountries
        .filter(
          /* do not show in the dropdown countries we shouldn't send */
          country => !cannotReceiveCountries.includes(country.countryCode)
        )
        .map(country => {
          const { countryCode, countryName } = country
          return (
            <option key={countryCode} value={countryCode}>
              {countryName}
            </option>
          )
        })
    }
    if (isDw2Repaint()) {
      let countries
      if (isMgm) {
        countries = this.props.griffinSupportedCountries
          .filter(
            /* do not show in the dropdown countries we shouldn't send */
            country => country.countryCode === this.props.countryCode
          )
          .map(country => {
            const { countryCode, countryName } = country
            return {
              value: countryCode,
              primaryText: countryName,
            }
          })
      } else {
        countries = this.props.griffinSupportedCountries
          .filter(
            /* do not show in the dropdown countries we shouldn't send */
            country => !cannotReceiveCountries.includes(country.countryCode)
          )
          .map(country => {
            const { countryCode, countryName } = country
            return {
              value: countryCode,
              primaryText: countryName,
            }
          })
      }
      countries = !isMgm
        ? ({ value: '', primaryText: i18n('recipientInfo.defaultCountry') },
          countries)
        : countries
      return (
        <Container>
          <Row className={rowMarginBottom}>
            <Col>
              <PPTextInput
                type="text"
                name="recipientName"
                id="recipientName"
                data-nemo="recipient-name"
                required
                pattern="(^.+$)"
                label={i18n('recipientInfo.nameLabel')}
                value={this.props.name}
                onChange={this.handleChangeName}
                theme="v2"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <DropdownMenu
                required
                id="recipientCountry"
                label={i18n('recipientInfo.defaultCountry')}
                options={countries}
                data-nemo="recipient-country"
                value={this.props.countryCode}
                onChange={this.handleChangeCountryCode}
              />
            </Col>
          </Row>
        </Container>
      )
    }
    return (
      <Container css={styleOverrides.container}>
        <div className="vx_form-group">
          <label htmlFor="recipientName">
            {i18n('recipientInfo.nameLabel')}
          </label>
          {showENSubTextRTR && (
            <div style={STYLES.rtrSubText}>
              {enRTRStrings['recipientInfo.nameLabel']}
            </div>
          )}
          <TextInput
            type="text"
            name="recipientName"
            id="recipientName"
            data-nemo="recipient-name"
            className="vx_form-control vx_form-control_complex"
            required
            pattern="(^.+$)"
            label={i18n('placeholder.recipientNamePlaceholder')}
            value={this.props.name}
            onChange={this.handleChangeName}
          />
        </div>

        <div className="vx_form-group">
          <label htmlFor="recipientCountry">
            {i18n('recipientInfo.defaultCountry')}
          </label>
          {showENSubTextRTR && (
            <div style={STYLES.rtrSubText}>
              {enRTRStrings['recipientInfo.defaultCountry']}
            </div>
          )}
          {/* disabling linting requirement, but should be up to the dev what is needed */
          /* eslint-disable-next-line jsx-a11y/no-onchange */}
          <select
            id="recipientCountry"
            className="vx_form-control"
            data-nemo="recipient-country"
            required
            value={this.props.countryCode}
            onChange={this.handleChangeCountryCode}
          >
            {!isMgm && (
              <option value="">{i18n('recipientInfo.defaultCountry')}</option>
            )}
            {options}
          </select>
        </div>
      </Container>
    )
  }
}

export default connect(({ unilateral, server, external }) => ({
  name: unilateral.name,
  countryCode: unilateral.countryCode,
  griffinSupportedCountries: server.griffinSupportedCountries,
  isMgm: server.isMgm,
}))(UnilateralFields)
